/**
 * Alerts Service
 * CRUD for alert rules, active alerts management, and evaluation trigger
 */

import { supabase } from "./supabase";

// ============================================================================
// TYPES
// ============================================================================

export interface AlertRule {
  id: string;
  name: string;
  metric: string;
  function_name: string | null;
  operator: string;
  threshold: number;
  severity: string;
  evaluation_window_minutes: number;
  enabled: boolean;
  notify_email: boolean;
  notify_in_app: boolean;
  notification_email: string | null;
  cooldown_minutes: number;
  is_default: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface SystemAlert {
  id: string;
  alert_rule_id: string;
  function_name: string | null;
  metric: string;
  severity: string;
  current_value: number | null;
  threshold: number;
  message: string;
  status: string;
  acknowledged_by: string | null;
  acknowledged_at: string | null;
  resolved_at: string | null;
  email_sent: boolean;
  created_at: string;
}

export interface AlertRuleInput {
  name: string;
  metric: string;
  function_name: string;
  operator?: string;
  threshold: number;
  severity: string;
  evaluation_window_minutes?: number;
  enabled?: boolean;
  notify_email?: boolean;
  notify_in_app?: boolean;
  notification_email?: string | null;
  cooldown_minutes?: number;
}

export interface EvaluationResult {
  rule_id: string;
  rule_name: string;
  action: string;
  metric_value: number;
}

// ============================================================================
// ALERT RULES CRUD
// ============================================================================

export const fetchAlertRules = async (): Promise<AlertRule[]> => {
  const { data, error } = await supabase
    .from("alert_rules")
    .select("*")
    .order("function_name")
    .order("severity", { ascending: false });

  if (error) {
    console.error("Failed to fetch alert rules:", error);
    return [];
  }
  return (data as AlertRule[]) || [];
};

export const createAlertRule = async (
  rule: AlertRuleInput,
): Promise<AlertRule | null> => {
  const { data, error } = await supabase
    .from("alert_rules")
    .insert({
      name: rule.name,
      metric: rule.metric,
      function_name: rule.function_name,
      operator: rule.operator || "gt",
      threshold: rule.threshold,
      severity: rule.severity,
      evaluation_window_minutes: rule.evaluation_window_minutes || 60,
      enabled: rule.enabled !== false,
      notify_email: rule.notify_email !== false,
      notify_in_app: rule.notify_in_app !== false,
      notification_email: rule.notification_email || null,
      cooldown_minutes: rule.cooldown_minutes || 60,
    })
    .select()
    .single();

  if (error) {
    console.error("Failed to create alert rule:", error);
    throw error;
  }
  return data as AlertRule;
};

export const updateAlertRule = async (
  id: string,
  updates: Partial<AlertRuleInput & { enabled: boolean }>,
): Promise<AlertRule | null> => {
  const { data, error } = await supabase
    .from("alert_rules")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Failed to update alert rule:", error);
    throw error;
  }
  return data as AlertRule;
};

export const deleteAlertRule = async (id: string): Promise<void> => {
  const { error } = await supabase.from("alert_rules").delete().eq("id", id);

  if (error) {
    console.error("Failed to delete alert rule:", error);
    throw error;
  }
};

// ============================================================================
// SYSTEM ALERTS
// ============================================================================

export const fetchActiveAlerts = async (): Promise<SystemAlert[]> => {
  const { data, error } = await supabase
    .from("system_alerts")
    .select("*")
    .in("status", ["active", "acknowledged"])
    .order("created_at", { ascending: false })
    .limit(20);

  if (error) {
    console.error("Failed to fetch active alerts:", error);
    return [];
  }
  return (data as SystemAlert[]) || [];
};

export const fetchSystemAlerts = async (
  filters: { status?: string; severity?: string } = {},
  page: number = 1,
  pageSize: number = 25,
): Promise<{ data: SystemAlert[]; count: number }> => {
  let query = supabase
    .from("system_alerts")
    .select("*", { count: "exact" })
    .order("created_at", { ascending: false });

  if (filters.status) {
    query = query.eq("status", filters.status);
  }
  if (filters.severity) {
    query = query.eq("severity", filters.severity);
  }

  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  query = query.range(from, to);

  const { data, error, count } = await query;

  if (error) {
    console.error("Failed to fetch system alerts:", error);
    return { data: [], count: 0 };
  }
  return { data: (data as SystemAlert[]) || [], count: count || 0 };
};

export const acknowledgeAlert = async (
  id: string,
  userId: string,
): Promise<void> => {
  const { error } = await supabase
    .from("system_alerts")
    .update({
      status: "acknowledged",
      acknowledged_by: userId,
      acknowledged_at: new Date().toISOString(),
    })
    .eq("id", id);

  if (error) {
    console.error("Failed to acknowledge alert:", error);
    throw error;
  }
};

export const resolveAlert = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from("system_alerts")
    .update({
      status: "resolved",
      resolved_at: new Date().toISOString(),
    })
    .eq("id", id);

  if (error) {
    console.error("Failed to resolve alert:", error);
    throw error;
  }
};

// ============================================================================
// EVALUATION TRIGGER
// ============================================================================

export const triggerAlertEvaluation = async (): Promise<EvaluationResult[]> => {
  const { data, error } = await supabase.rpc("evaluate_alert_rules");

  if (error) {
    console.error("Failed to trigger alert evaluation:", error);
    throw error;
  }
  return (data as EvaluationResult[]) || [];
};
